// See NotificationCenterPage.spec.md for NCP-FR-004, NCP-FR-006 and NCP-FR-010, NotificationBar.spec.md for
// NB-FR-005 (the bar shows the same columns)

import DateCell from 'core/components/listTable/cells/DateCell'
import Badge from 'core/elements/badge'
import GridDefaultCell from 'core/elements/grid/cells/GridDefaultCell'
import GridLinkCell from 'core/elements/grid/cells/GridLinkCell'
import type { GridViewColumn } from 'core/elements/grid/Grid'
import Text from 'core/elements/Text'
import { routeManager } from 'core/utils/routes'
import { capitalizeString } from 'utils/misc'
import {
  EMPTY_VALUE,
  type EventRowContext,
  formatActionName,
  formatEventWithObjectType,
  formatInitiator,
  formatObject,
  formatResourceType,
  getAuditEventId,
  getObjectLinkType,
  getResultVariant,
  type LinkableResource,
  parseTimestamp,
  resolveResourceName,
} from './helpers'
import type { AuditEvent } from './models'

export interface EventRow extends AuditEvent {
  id: string
  event: string
  objectType: string
  // NCP-FR-004: What the Event column shows (the event with its object type), worked out once
  eventLabel: string
  object: string
  // NCP-FR-010: The details page the Object opens, if it can be opened
  objectLinkType?: LinkableResource
  initiator: string
}

const TRIGGERED_FORMAT = 'MM/DD/YYYY, hh:mm:ss A'

const TriggeredCell = ({ value }: { value?: string }) =>
  parseTimestamp(value) !== null ? (
    <DateCell value={value} format={TRIGGERED_FORMAT} />
  ) : (
    <Text variant="body2">{EMPTY_VALUE}</Text>
  )

// NCP-FR-004: Display only; sorting uses the event value, search the shown label (NCP-FR-006)
const EventCell = ({ item }: { item?: EventRow }) => {
  const text = item?.eventLabel ?? EMPTY_VALUE
  return <GridDefaultCell title={text}>{text}</GridDefaultCell>
}

// NCP-FR-010: Paths are built when a cell renders; routes are registered at runtime
const OBJECT_ROUTES: Record<LinkableResource, () => { path: (params: { id: string }) => string }> =
  {
    vm: () => routeManager.routes.openstack.vmDetails,
    volume: () => routeManager.routes.openstack.volumeDetails,
    snapshot: () => routeManager.routes.openstack.volumeSnapshotDetails,
  }

const getObjectPath = ({ objectLinkType, resource_id: id }: EventRow): string | undefined =>
  objectLinkType ? OBJECT_ROUTES[objectLinkType]().path({ id }) : undefined

// NCP-FR-010: The Object (name, else ID) links to its details page when it can be opened
const ObjectCell = ({ item }: { item?: EventRow }) => (
  <GridLinkCell item={item} routeToFn={(row) => (row ? getObjectPath(row) : undefined)}>
    {item?.object ?? EMPTY_VALUE}
  </GridLinkCell>
)

const ResultCell = ({ value }: { value?: string }) =>
  value ? (
    <Badge variant={getResultVariant(value)} text={capitalizeString(value)} />
  ) : (
    <Text variant="body2">{EMPTY_VALUE}</Text>
  )

export const eventColumns: GridViewColumn<EventRow>[] = [
  { key: 'event', label: 'Event', CellComponent: EventCell },
  { key: 'timestamp_utc', label: 'Triggered', CellComponent: TriggeredCell },
  { key: 'action_result', label: 'Result', CellComponent: ResultCell },
  // Shown in the Event column; kept (hidden by default) so it can still be sorted on and shown
  { key: 'objectType', label: 'Object Type', display: false },
  { key: 'object', label: 'Object', CellComponent: ObjectCell },
  { key: 'initiator', label: 'Initiator' },
  { key: 'region', label: 'Region', display: false },
  { key: 'project_id', label: 'Project', display: false },
  { key: 'source_ip', label: 'Source IP', display: false },
  { key: 'request_id', label: 'Request ID', display: false },
]

// NCP-FR-006: search the formatted values users see, newest-first sort is set on the table
export const eventSearchTargets: Array<keyof EventRow> = [
  'event',
  'object',
  'objectType',
  'initiator',
]
// NCP-FR-006: The Event column shows the event with its object type ("Authenticate : User"), so
// searching Event matches that label, not just the event name
export const eventSearchFunctions = [
  {
    property: 'event',
    searchFn: (_value: unknown, search: string, item?: EventRow): boolean =>
      (item?.eventLabel ?? '').toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  },
]
export const eventSearchTargetLabels = {
  event: 'Event',
  object: 'Object',
  objectType: 'Object Type',
  initiator: 'Initiator',
}

// NCP-FR-010: `context` (getEventRowContext over all loaded events) fills in names older records
// lack and decides whether the Object links anywhere; without it the row shows only its own data.
// The row ID is the event's stable ID, so rows keep their keys across polls; `repeat` tells apart
// identical records (0 for the first).
export const toEventRow = (
  event: AuditEvent,
  repeat = 0,
  context?: EventRowContext,
  baseId = getAuditEventId(event),
): EventRow => {
  const eventName = formatActionName(event.action_name)
  const objectType = formatResourceType(event.resource_type)
  return {
    ...event,
    id: repeat ? `${baseId}#${repeat}` : baseId,
    event: eventName,
    objectType,
    eventLabel: formatEventWithObjectType(eventName, objectType),
    object: formatObject({ ...event, resource_name: resolveResourceName(event, context) }),
    objectLinkType: getObjectLinkType(event, context),
    initiator: formatInitiator(event),
  }
}

// Rows for a list of events, numbering repeats of identical records so every row ID is unique
export const toEventRows = (events: AuditEvent[], context?: EventRowContext): EventRow[] => {
  const seen = new Map<string, number>()
  return events.map((event) => {
    const id = getAuditEventId(event)
    const repeat = seen.get(id) ?? 0
    seen.set(id, repeat + 1)
    return toEventRow(event, repeat, context, id)
  })
}
